import React, { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "../../Utils/axios";
import {
  Button,
  Grid,
  Typography,
  Box,
  Pagination,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  TableCell,
  Paper,
  Divider,
  Chip,
  IconButton,
  Stack,
  Avatar,
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Fade,
  Tooltip,
} from "@mui/material";
import bgImage from "../../Assets/images/matching-plt-header-bg.png";
// Icons
import {
  Person,
  ShoppingBag,
  EventAvailable,
  Subscriptions,
  Email,
  LocationOn,
  Phone,
  VideoLibrary,
  ConfirmationNumber,
  FileDownload,
  Visibility,
  ChatBubbleOutline,
  OpenInNew,
  Close,
  Computer,
  Stars,
  CalendarMonth,
  LocalActivity,
  AccountCircle,
  Badge,
  Payments,
} from "@mui/icons-material";
import { FaFilePdf, FaEye } from "react-icons/fa";

import GlobalContext from "../../Context/GlobalContext";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import roundBg from "../../Assets/images/round-background-matching-plat.png";
import ConversationModel from "./ConversationModel";
import ConversationDialog from "./ConversationDialog";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import AccessTime from "@mui/icons-material/AccessTime";
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import VideoCall from "@mui/icons-material/VideoCall";
import History from "@mui/icons-material/History";
function ClientProfile() {
  const { t } = useTranslation();
  const { id_client } = useParams();
  const context = useContext(GlobalContext);
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("info");
  const [cmdPage, setCmdPage] = useState(1);
  const [resPage, setResPage] = useState(1);
  const [subPage, setSubPage] = useState(1);
  const [formData, setFormData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isResDialogOpen, setIsResDialogOpen] = useState(false);
  const [isSubDialogOpen, setIsSubDialogOpen] = useState(false);
  const [selectedRes, setSelectedRes] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [currentConv, setCurrentConv] = useState(null);
  const fetchClient = async () => {
    const token = localStorage.getItem("access_token_story");

    const res = await axios.get(
      `${process.env.REACT_APP_APIURL}/client/g/get/p/profile`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return res.data.result;
  };

  const {
    data: client,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["client-profile"],
    queryFn: fetchClient,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (client) {
      setFormData({
        nom_client: client.nom_client || "",
        prenom_client: client.prenom_client || "",
        email_client: client.email_client || "",
        addresse_client: client.addresse_client || "",
        ville_client: client.ville_client || "",
        cp_client: client.cp_client || "",
        province_client: client.province_client || "",
        phone_client: client.phone_client || "",
      });
    }
  }, [client]);

  if (isLoading)
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <Typography>{t("loadingData")}</Typography>
      </Box>
    );
  if (isError || !client)
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <Typography color="error">{t("errorLoadingData")}</Typography>
      </Box>
    );

  // Logique de données
  const allReservations = [
    ...(client.reservations || []).map((r) => ({
      ...r,
      activityType: "consultation",
      isParticipant: false,
    })),
    ...(client.participants || []).map((p) => ({
      ...p,
      activityType: "atelier",
      isParticipant: true,
      id_offre_reservation: p.id_wsp,
    })),
  ];
  const sortedRes = allReservations.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const paginatedRes = sortedRes.slice((resPage - 1) * 6, 6 * resPage);

  const successfulCmds = (client.commands || [])
    .filter((c) => c.etat_cmd === "SUCCESS")
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const paginatedCmds = successfulCmds.slice((cmdPage - 1) * 6, 6 * cmdPage);

  const sortedSubs = (client.subscriptions || []).sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const paginatedSubs = sortedSubs.slice((subPage - 1) * 6, 6 * subPage);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    try {
      toast.success(t("profileUpdated"));
      queryClient.setQueryData(["client", id_client], (old) => ({
        ...old,
        ...formData,
      }));
    } catch (err) {
      toast.error(t("errorUpdating"));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownloadInvoice = async (ref) => {
    try {
      const res = await axios.get(`/offer/command/invoice/${ref}`, {
        responseType: "arraybuffer",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob), "_blank");
    } catch (err) {
      alert(t("errorFetchingInvoice"));
    }
  };

  const formatDate = (d) =>
    new Date(d)
      .toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", " à");

  // Modern UI Colors & Styles
  const COLORS = {
    primary: "#2B3674", // Bleu Nuit
    secondary: "#d6a77a", // Taupe/Gold
    bg: "#F4F7FE",
    textMain: "#1B2559",
    textSec: "#A3AED0",
    white: "#FFFFFF",
  };

  const glassStyle = {
    borderRadius: "20px",
    border: "none",
    boxShadow: "0px 18px 40px rgba(112, 144, 176, 0.12)",
    bgcolor: COLORS.white,
  };

  // 1. Sécurisez la fonction getResDate
  const getResDate = () => {
    // Si selectedRes n'est pas encore chargé, on retourne null
    if (!selectedRes) return null;

    if (selectedRes.isParticipant) {
      return selectedRes.session?.date_wss;
    }
    return selectedRes.start_time_reservation;
  };

  // 2. Assurez-vous que l'appel de la fonction ne plante pas
  const resDate = getResDate();
  const normalizeFormat = (format) => {
    const map = {
      zoom: "zoom",

      presentiel: "presentiel",
      en_presentiel: "presentiel",

      hybride: "hybride",
      en_presentiel_et_zoom: "hybride",
    };

    return map[format] || "unknown";
  };

  const getOffreDisplay = (selectedRes) => {
    const isSession = selectedRes?.isParticipant;

    const source = isSession
      ? selectedRes?.session?.workshop
      : selectedRes?.offre?.info_offre;

    const rawFormat = isSession ? source?.format_ws : source?.format_offre;

    const format = normalizeFormat(rawFormat);

    const isZoom = format === "zoom";
    const isPresentiel = format === "presentiel";
    const isHybride = format === "hybride";

    const address = source?.address_ws;

    return {
      label: isZoom
        ? "En ligne (Zoom)"
        : isPresentiel
          ? address || "Présentiel"
          : isHybride
            ? `Hybride (Zoom + ${address || "adresse"})`
            : "Format non défini",

      isOnline: isZoom,
      isPhysical: isPresentiel || isHybride,

      location: !isZoom
        ? {
            address_ws: source?.address_ws,
            city_ws: source?.city_ws,
            postal_code_ws: source?.postal_code_ws,
            country_ws: source?.country_ws,
            location_note_ws: source?.location_note_ws,
          }
        : null,
    };
  };

  const offreDisplay = getOffreDisplay(selectedRes);
  return (
    <Box
      sx={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed", // Optionnel : pour un effet parallaxe moderne
        minHeight: "100vh",
        pt: 10,
        pb: 6,
      }}>
      {" "}
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          sx={{
            mb: 5,
            fontWeight: 800,
            color: COLORS.primary,
            textAlign: "center",
            letterSpacing: "-0.5px",
          }}>
          {t("clientSpace")}
        </Typography>

        <Grid container spacing={3}>
          {/* MENU GAUCHE */}
          <Grid item xs={12} md={4}>
            <Card sx={{ ...glassStyle, p: 2 }}>
              <Box sx={{ textAlign: "center", my: 3 }}>
                <Avatar
                  sx={{
                    width: 70,
                    height: 70,
                    mx: "auto",
                    bgcolor: COLORS.secondary,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 2,
                  }}>
                  {client.nom_client?.charAt(0)}
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: COLORS.textMain }}>
                  {client.prenom_client} {client.nom_client}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.textSec }}>
                  {client.email_client}
                </Typography>
              </Box>
              <Divider sx={{ opacity: 0.6 }} />
              <List sx={{ mt: 2 }}>
                {[
                  {
                    id: "info",
                    text: t("accountInformation"),
                    icon: <Badge />,
                  },
                  {
                    id: "profile",
                    text: t("accountSettings"),
                    icon: <AccountCircle />,
                  },
                  { id: "commande", text: t("commands"), icon: <Payments /> },
                  {
                    id: "reservation",
                    text: t("reservations"),
                    icon: <EventAvailable />,
                  },
                  {
                    id: "subscriptions",
                    text: t("subscriptionsclient"),
                    icon: <Subscriptions />,
                  },
                ].map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={activeTab === item.id}
                    onClick={() => setActiveTab(item.id)}
                    sx={{
                      borderRadius: "12px",
                      mb: 1,
                      transition: "0.3s",
                      "&.Mui-selected": {
                        bgcolor: COLORS.primary,
                        color: "white",
                        "& .MuiListItemIcon-root": { color: "white" },
                      },
                      "&:hover": {
                        bgcolor:
                          activeTab === item.id ? COLORS.primary : "#E9EDF7",
                      },
                    }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: activeTab === item.id ? "white" : COLORS.textSec,
                      }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Card>
          </Grid>

          {/* CONTENU PRINCIPAL */}
          <Grid item xs={12} md={8}>
            <Card sx={{ ...glassStyle, minHeight: 550, p: { xs: 2, md: 4 } }}>
              <Fade in={true} timeout={600}>
                <Box>
                  {/* --- SECTION INFORMATIONS (MODERNISÉE) --- */}
                  {activeTab === "info" && (
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: COLORS.primary, mb: 4 }}>
                        {t("accountInformation")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: COLORS.textSec,
                          fontWeight: 500,
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}>
                        <Stars sx={{ fontSize: 18, color: COLORS.secondary }} />
                        Voici vos crédits disponibles à utiliser sur la
                        plateforme :
                      </Typography>
                      {/* STATS CARDS RÉINTÉGRÉES ICI */}
                      <Grid container spacing={2} sx={{ mb: 5 }}>
                        {[
                          {
                            label: t("remainingVideos"),
                            val: client.nbr_video,
                            color: "#4318FF",
                            icon: <VideoLibrary fontSize="small" />,
                            desc: "Jetons restants",
                          },
                          {
                            label: t("remainingWorkshops"),
                            val: client.nbr_workshops,
                            color: "#6AD2FF",
                            icon: <LocalActivity fontSize="small" />,
                            desc: "Ateliers",
                          },
                          {
                            label: t("remainingOffers"),
                            val: client.nbr_offre,
                            color: "#01B574",
                            icon: <Stars fontSize="small" />,
                            desc: "Consultations",
                          },
                        ].map((stat, i) => (
                          <Grid item xs={12} sm={4} key={i}>
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: "15px",
                                bgcolor: COLORS.bg,
                                border: "1px solid #E9EDF7",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}>
                              <Avatar
                                sx={{
                                  bgcolor: "white",
                                  color: stat.color,
                                  width: 40,
                                  height: 40,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                                }}>
                                {stat.icon}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 800,
                                    color: COLORS.textMain,
                                    lineHeight: 1,
                                  }}>
                                  {stat.val}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: COLORS.textSec,
                                    fontWeight: 600,
                                    fontSize: "0.65rem",
                                    textTransform: "uppercase",
                                  }}>
                                  {stat.desc}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      {/* INFOS PERSONNELLES */}
                      <Grid container spacing={3}>
                        {[
                          {
                            label: t("lastName"),
                            val: client.nom_client,
                            icon: <Person fontSize="inherit" />,
                          },
                          {
                            label: t("firstName"),
                            val: client.prenom_client,
                            icon: <Person fontSize="inherit" />,
                          },
                          {
                            label: t("email"),
                            val: client.email_client,
                            icon: <Email fontSize="inherit" />,
                          },
                          {
                            label: t("phone"),
                            val: client.phone_client,
                            icon: <Phone fontSize="inherit" />,
                          },
                          {
                            label: t("address"),
                            val: client.addresse_client,
                            icon: <LocationOn fontSize="inherit" />,
                          },
                          {
                            label: t("city"),
                            val: `${client.cp_client} ${client.ville_client}`,
                            icon: <LocationOn fontSize="inherit" />,
                          },
                        ].map((field, i) => (
                          <Grid item xs={12} sm={6} key={i}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 0.5 }}>
                              <Box
                                sx={{
                                  color: COLORS.secondary,
                                  display: "flex",
                                }}>
                                {field.icon}
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: COLORS.textSec,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}>
                                {field.label}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: COLORS.textMain,
                                pl: 3,
                              }}>
                              {field.val || "—"}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* ONGLET: MODIFICATION PROFILE */}
                  {activeTab === "profile" && (
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: COLORS.primary, mb: 4 }}>
                        {t("accountSettings")}
                      </Typography>
                      <form onSubmit={handleUpdateProfile}>
                        <Grid container spacing={2}>
                          {Object.keys(formData).map((key) => (
                            <Grid
                              item
                              xs={12}
                              sm={key === "addresse_client" ? 12 : 6}
                              key={key}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: COLORS.textSec,
                                  ml: 1,
                                }}>
                                {t(key.replace("_client", ""))}
                              </Typography>
                              <input
                                className="form-control"
                                style={{
                                  width: "100%",
                                  padding: "12px",
                                  borderRadius: "12px",
                                  border: "1px solid #E2E8F0",
                                  marginTop: "5px",
                                  backgroundColor: "#F8FAFC",
                                }}
                                value={formData[key]}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    [key]: e.target.value,
                                  })
                                }
                                type={key.includes("email") ? "email" : "text"}
                              />
                            </Grid>
                          ))}
                        </Grid>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isUpdating}
                          sx={{
                            mt: 4,
                            borderRadius: "12px",
                            py: 1.5,
                            px: 6,
                            bgcolor: COLORS.primary,
                            fontWeight: 700,
                            textTransform: "none",
                            "&:hover": { bgcolor: "#1B2559" },
                          }}>
                          {isUpdating ? "..." : t("update")}
                        </Button>
                      </form>
                    </Box>
                  )}

                  {/* ONGLET: COMMANDES */}
                  {activeTab === "commande" && (
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: COLORS.primary, mb: 4 }}>
                        {t("commands")}
                      </Typography>
                      {paginatedCmds.length > 0 ? (
                        <>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              border: "1px solid #E9EDF7",
                              borderRadius: "15px",
                            }}>
                            <Table>
                              <TableHead sx={{ bgcolor: "#F4F7FE" }}>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("commandRef")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("amountPaid")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("type")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("invoice")}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedCmds.map((c) => {
                                  // Logique de mapping pour les types et couleurs
                                  const getTypeDetails = (type) => {
                                    switch (type) {
                                      case "workshop":
                                        return {
                                          label: "Atelier",
                                          color: "#4318FF",
                                          bg: "#EEF2FF",
                                        };
                                      case "offer":
                                        return {
                                          label: "Consultation",
                                          color: "#01B574",
                                          bg: "#E6F7F0",
                                        };
                                      case "subscription":
                                        return {
                                          label: "Abonnement",
                                          color: "#FFB547",
                                          bg: "#FFF8ED",
                                        };
                                      case "PACK_VIDEO":
                                        return {
                                          label: "Pack Vidéo",
                                          color: "#6AD2FF",
                                          bg: "#EAF9FF",
                                        };
                                      default:
                                        return {
                                          label: type,
                                          color: COLORS.primary,
                                          bg: COLORS.bg,
                                        };
                                    }
                                  };

                                  const details = getTypeDetails(c.type_cmd);

                                  return (
                                    <TableRow key={c.id_cmd} hover>
                                      <TableCell>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 700,
                                            color: COLORS.textMain,
                                          }}>
                                          {c.ref_cmd}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: COLORS.textSec }}>
                                          {formatDate(c.created_at)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 800,
                                          color: COLORS.textMain,
                                        }}>
                                        {c.amount_paid} €
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={details.label}
                                          size="small"
                                          sx={{
                                            fontWeight: 800,
                                            fontSize: "0.7rem",
                                            bgcolor: details.bg,
                                            color: details.color,
                                            border: `1px solid ${details.color}20`, // Bordure très légère pour le relief
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <IconButton
                                          onClick={() =>
                                            handleDownloadInvoice(c.ref_cmd)
                                          }
                                          sx={{ color: "#E31A1A" }}>
                                          <FaFilePdf size={18} />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Pagination
                            count={Math.ceil(successfulCmds.length / 6)}
                            page={cmdPage}
                            onChange={(e, v) => setCmdPage(v)}
                            sx={{
                              mt: 3,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          />
                        </>
                      ) : (
                        <Typography color="textSecondary">
                          {t("noCommands")}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* ONGLET: RESERVATIONS */}
                  {activeTab === "reservation" && (
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: COLORS.primary, mb: 1 }} // Réduit le mb de 4 à 1 pour rapprocher la note
                      >
                        {t("reservations")}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          mb: 4, // On reporte le grand margin-bottom ici pour espacer le reste du contenu
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontStyle: "italic",
                        }}>
                        <span
                          style={{ color: COLORS.primary, fontWeight: "bold" }}>
                          *
                        </span>
                        {t(
                          "dialogue_intervenant_hint",
                        )}
                      </Typography>
                      {paginatedRes.length > 0 ? (
                        <>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              border: "1px solid #E9EDF7",
                              borderRadius: "15px",
                            }}>
                            <Table>
                              <TableHead sx={{ bgcolor: "#F4F7FE" }}>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("offer")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("startDate")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("status")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("details")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("Message")} *
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedRes.map((r, idx) => {
                                  // Logique de mapping des données selon le type
                                  const isWorkshop = r.isParticipant;
                                  const title = isWorkshop
                                    ? r.session?.workshop?.title_ws
                                    : r.offre?.info_offre?.titre_offre;

                                  const intervenant = isWorkshop
                                    ? `${r.session?.workshop?.intervenant?.prenom_user} ${r.session?.workshop?.intervenant?.nom_user}`
                                    : `${r.user?.prenom_user} ${r.user?.nom_user}`;

                                  const format = isWorkshop
                                    ? r.session?.workshop?.format_ws === "zoom"
                                      ? "Zoom"
                                      : "Présentiel"
                                    : r.offre?.info_offre?.format_offre ===
                                        "zoom"
                                      ? "Zoom"
                                      : "Présentiel";

                                  const dateDisplay = isWorkshop
                                    ? new Date(
                                        r.session?.date_wss,
                                      ).toLocaleString(undefined, {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : new Date(
                                        r.start_time_reservation,
                                      ).toLocaleString(undefined, {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      });
                                  return (
                                    <TableRow key={idx} hover>
                                      <TableCell>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 700,
                                            color: COLORS.textMain,
                                          }}>
                                          {title}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: COLORS.secondary,
                                            display: "block",
                                            fontWeight: 600,
                                          }}>
                                          {isWorkshop
                                            ? t("atelier")
                                            : t("consultation")}{" "}
                                          — {format}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: COLORS.textSec,
                                            fontStyle: "italic",
                                          }}>
                                          {t("with")} {intervenant}
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          color: COLORS.textMain,
                                          fontWeight: 500,
                                        }}>
                                        {dateDisplay}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={
                                            isWorkshop
                                              ? "Accepté"
                                              : r.status_reservation ===
                                                  "pending"
                                                ? t("enAttente")
                                                : r.status_reservation
                                          }
                                          size="small"
                                          sx={{
                                            fontWeight: 700,
                                            bgcolor:
                                              isWorkshop ||
                                              r.status_reservation ===
                                                "accepted"
                                                ? "#E6FFF5"
                                                : "#FFF4E5",
                                            color:
                                              isWorkshop ||
                                              r.status_reservation ===
                                                "accepted"
                                                ? "#05CD99"
                                                : "#FF9800",
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <IconButton
                                          onClick={() => {
                                            setSelectedRes(r);
                                            setIsResDialogOpen(true);
                                          }}
                                          sx={{ color: COLORS.textSec }}>
                                          <FaEye size={18} />
                                        </IconButton>
                                      </TableCell>

                                      <TableCell>
                                        <IconButton
                                          onClick={() => {
                                            const conversationToOpen =
                                              r.isParticipant
                                                ? r.session?.conversation
                                                : r.conversation;

                                            if (conversationToOpen) {
                                              setCurrentConv(
                                                conversationToOpen,
                                              );
                                              setIsChatOpen(true);
                                            } else {
                                              toast.error(
                                                "Aucune conversation disponible",
                                              );
                                            }
                                          }}
                                          sx={{
                                            bgcolor: "#EEF2FF",
                                            color: COLORS.primary,
                                            "&:hover": {
                                              bgcolor: "#DCE7FF",
                                            },
                                          }}>
                                          <ChatBubbleOutline />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Pagination
                            count={Math.ceil(sortedRes.length / 6)}
                            page={resPage}
                            onChange={(e, v) => setResPage(v)}
                            sx={{
                              mt: 3,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          />
                        </>
                      ) : (
                        <Typography color="textSecondary">
                          {t("noReservations")}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* ONGLET: ABONNEMENTS */}
                  {activeTab === "subscriptions" && (
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: COLORS.primary, mb: 4 }}>
                        {t("subscriptionsclient")}
                      </Typography>
                      {paginatedSubs.length > 0 ? (
                        <>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              border: "1px solid #E9EDF7",
                              borderRadius: "15px",
                            }}>
                            <Table>
                              <TableHead sx={{ bgcolor: "#F4F7FE" }}>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("subscriptionName")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("renewalDate")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("status")}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      color: COLORS.textSec,
                                    }}>
                                    {t("details")}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedSubs.map((s, idx) => (
                                  <TableRow key={idx} hover>
                                    <TableCell
                                      sx={{
                                        fontWeight: 700,
                                        color: COLORS.textMain,
                                      }}>
                                      {s.subscription?.name_subs}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontSize: "0.8rem",
                                        color: COLORS.textMain,
                                      }}>
                                      {s.subscription?.expiration_date_subs}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={
                                          s.status_client_subs === "1"
                                            ? t("active")
                                            : t("inactive")
                                        }
                                        size="small"
                                        color={
                                          s.status_client_subs === "1"
                                            ? "success"
                                            : "default"
                                        }
                                        sx={{ fontWeight: 700 }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <IconButton
                                        onClick={() => {
                                          setSelectedSub(s);
                                          setIsSubDialogOpen(true);
                                        }}>
                                        <FaEye size={18} />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Pagination
                            count={Math.ceil(client.subscriptions?.length / 6)}
                            page={subPage}
                            onChange={(e, v) => setSubPage(v)}
                            sx={{
                              mt: 3,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          />
                        </>
                      ) : (
                        <Typography color="textSecondary">
                          {t("noSubscriptions")}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Fade>
            </Card>
          </Grid>
        </Grid>
      </Container>
      {/* DIALOG: DETAILS RÉSERVATION */}
      <Dialog
        open={isResDialogOpen}
        onClose={() => setIsResDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "#FFFFFF",
            backgroundImage:
              "radial-gradient(at 0% 0%, rgba(79, 70, 229, 0.05) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.05) 0px, transparent 50%)",
            boxShadow: "0 30px 60px -12px rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.8)",
          },
        }}>
        <DialogTitle
          sx={{
            p: 3,
            pb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                color: "#0F172A",
                letterSpacing: "-0.5px",
              }}>
              {t("reservationDetails")}
            </Typography>
            {/* Date Stylisée */}
            {resDate && (
              <Typography
                variant="caption"
                sx={{
                  color: COLORS.primary,
                  fontWeight: 700,
                  textTransform: "capitalize",
                }}>
                {new Date(resDate).toLocaleDateString("fr-FR", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => setIsResDialogOpen(false)}
            sx={{
              bgcolor: "#F1F5F9",
              "&:hover": { transform: "rotate(90deg)", bgcolor: "#E2E8F0" },
              transition: "0.3s",
            }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedRes && (
            <Stack spacing={3}>
              {/* Main Content Card */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: "24px",
                  bgcolor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  position: "relative",
                  overflow: "hidden",
                }}>
                {/* Badge Type */}
                <Chip
                  label={selectedRes.isParticipant ? "Atelier" : "Consultation"}
                  size="small"
                  sx={{
                    mb: 2,
                    fontWeight: 800,
                    fontSize: "0.65rem",
                    bgcolor: selectedRes.isParticipant ? "#EEF2FF" : "#F0FDF4",
                    color: selectedRes.isParticipant ? "#4F46E5" : "#16A34A",
                    border: "1px solid currentColor",
                  }}
                />

                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, mb: 1, color: "#1E293B" }}>
                  {selectedRes.isParticipant
                    ? selectedRes.session?.workshop?.title_ws
                    : selectedRes.offre?.info_offre?.titre_offre}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "#64748B", lineHeight: 1.6 }}>
                  {selectedRes.isParticipant
                    ? selectedRes.session?.workshop?.description_ws
                    : selectedRes.offre?.info_offre?.description_offre}
                </Typography>
              </Box>

              {/* Info Grid (Heure, Durée, Prix, Paiement) */}
              <Grid container spacing={1.5}>
                {/* Heure */}
                <Grid item xs={3}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: "20px",
                      border: "1px solid #F1F5F9",
                      height: "100%",
                    }}>
                    <AccessTime
                      sx={{ fontSize: 20, color: "#94A3B8", mb: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ fontWeight: 700, color: "#94A3B8" }}>
                      Heure
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                      {resDate
                        ? new Date(resDate).toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "--:--"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Durée */}
                <Grid item xs={3}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: "20px",
                      border: "1px solid #F1F5F9",
                      height: "100%",
                    }}>
                    <History sx={{ fontSize: 20, color: "#94A3B8", mb: 0.5 }} />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ fontWeight: 700, color: "#94A3B8" }}>
                      Durée
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                      {selectedRes.isParticipant
                        ? `${selectedRes.session?.workshop?.duration_ws} min`
                        : `${selectedRes.offre?.info_offre?.duree_offre} min`}
                    </Typography>
                  </Box>
                </Grid>

                {/* Prix */}
                <Grid item xs={3}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: "20px",
                      border: "1px solid #F1F5F9",
                      height: "100%",
                    }}>
                    <Payments
                      sx={{ fontSize: 20, color: "#94A3B8", mb: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ fontWeight: 700, color: "#94A3B8" }}>
                      Prix
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, fontSize: "0.8rem" }}>
                      {selectedRes.isParticipant
                        ? selectedRes.session?.workshop?.price_ws
                        : selectedRes.offre?.info_offre?.prix_offre}
                      €
                    </Typography>
                  </Box>
                </Grid>

                {/* Statut Paiement */}
                <Grid item xs={3}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: "20px",
                      height: "100%",
                      bgcolor: (
                        selectedRes.isParticipant
                          ? selectedRes.session?.is_paid_wss === 1
                          : selectedRes.offre?.info_offre?.is_paid_offre
                      )
                        ? "#DCFCE7"
                        : "#FEE2E2",
                    }}>
                    <VerifiedUser
                      sx={{
                        fontSize: 20,
                        color: (
                          selectedRes.isParticipant
                            ? selectedRes.session?.is_paid_wss === 1
                            : selectedRes.offre?.info_offre?.is_paid_offre
                        )
                          ? "#16A34A"
                          : "#EF4444",
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ fontWeight: 700, opacity: 0.7 }}>
                      {t("payment")}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 900, fontSize: "0.8rem" }}>
                      {(
                        selectedRes.isParticipant
                          ? selectedRes.session?.is_paid_wss === 1
                          : selectedRes.offre?.info_offre?.is_paid_offre
                      )
                        ? "Payé"
                        : "Attente"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Location & Format */}
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  p: 2,
                  bgcolor: "#F8FAFC",
                  borderRadius: "20px",
                  alignItems: "center",
                }}>
                {/* ICON */}
                <Box
                  sx={{
                    p: 1,
                    bgcolor: "#FFF",
                    borderRadius: "12px",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                  }}>
                  {offreDisplay.isOnline ? (
                    <VideoCall color="primary" />
                  ) : (
                    <LocationOn color="primary" />
                  )}
                </Box>

                {/* CONTENT */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#94A3B8", fontWeight: 700 }}>
                    Lieu & Format
                  </Typography>

                  {/* ONLINE */}
                  {offreDisplay.isOnline && (
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                      {offreDisplay.label}
                    </Typography>
                  )}

                  {/* PHYSICAL */}
                  {!offreDisplay.isOnline && offreDisplay.location && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        📍 Adresse
                      </Typography>

                      <Typography variant="body2">
                        {[
                          offreDisplay.location.address_ws,
                          offreDisplay.location.city_ws,
                          offreDisplay.location.postal_code_ws,
                          offreDisplay.location.country_ws,
                        ]
                          .filter(Boolean)
                          .join(", ") || "Adresse non renseignée"}
                      </Typography>

                      {offreDisplay.location.location_note_ws && (
                        <Typography variant="caption" sx={{ color: "gray" }}>
                          {offreDisplay.location.location_note_ws}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Stack>

              {/* Speaker Card (Dark Mode Contrast) */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "24px",
                  bgcolor: "#1E293B",
                  color: "#FFF",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      border: "2px solid rgba(255,255,255,0.2)",
                      bgcolor: COLORS.secondary,
                    }}
                    src={
                      selectedRes.session?.workshop?.intervenant?.photo_user
                    }>
                    {selectedRes.session?.workshop?.intervenant?.nom_user?.charAt(
                      0,
                    ) || selectedRes.user?.nom_user?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>
                      Intervenant
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {selectedRes.isParticipant
                        ? `${selectedRes.session?.workshop?.intervenant?.nom_user} ${selectedRes.session?.workshop?.intervenant?.prenom_user}`
                        : `${selectedRes.user?.nom_user} ${selectedRes.user?.prenom_user}`}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton
                  onClick={() => {
                    // On cherche la conversation soit dans la session (workshop)
                    // soit directement à la racine (offre)
                    const conversationToOpen = selectedRes.isParticipant
                      ? selectedRes.session?.conversation
                      : selectedRes.conversation;

                    if (conversationToOpen) {
                      setCurrentConv(conversationToOpen);
                      setIsChatOpen(true);
                    } else {
                      console.warn(
                        "Aucune conversation trouvée pour cette réservation",
                      );
                      // Optionnel: afficher une alerte ou un message à l'utilisateur
                    }
                  }}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "#FFF",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                  }}>
                  <ChatBubbleOutline />
                </IconButton>
              </Paper>

              {/* Join Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                href={
                  selectedRes.isParticipant
                    ? selectedRes.meeting_link_wsp
                    : selectedRes.info_conference?.[0]?.link_client_conf
                }
                target="_blank"
                sx={{
                  py: 2,
                  borderRadius: "20px",
                  background: `linear-gradient(45deg, ${COLORS.primary}, ${COLORS.secondary})`,
                  fontWeight: 800,
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: `0 12px 24px -10px ${COLORS.primary}`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 15px 30px -10px ${COLORS.primary}`,
                  },
                }}
                startIcon={<OpenInNew />}>
                {selectedRes.isParticipant
                  ? "Rejoindre l'Atelier"
                  : "Rejoindre la Consultation"}
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
      {/* DIALOG: DETAILS ABONNEMENT */}
      <Dialog
        open={isSubDialogOpen}
        onClose={() => setIsSubDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "24px" } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("subscriptionDetails")}
        </DialogTitle>
        <DialogContent>
          {selectedSub && (
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{ position: "relative", display: "inline-block", mb: 3 }}>
                <img src={roundBg} alt="bg" style={{ width: 100 }} />
                <Typography
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontWeight: 800,
                    color: "white",
                    fontSize: "0.6rem",
                    width: "70%",
                  }}>
                  {selectedSub.subscription?.name_subs}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ mb: 3, fontStyle: "italic", color: COLORS.textSec }}>
                "{selectedSub.subscription?.description_subs}"
              </Typography>
              <Grid
                container
                spacing={2}
                sx={{
                  bgcolor: COLORS.bg,
                  p: 2,
                  borderRadius: "16px",
                  textAlign: "left",
                }}>
                {[
                  { l: t("price"), v: `${selectedSub.price_client_subs} €` },
                  { l: t("dateBuy"), v: selectedSub.date_buy_client_subs },
                  {
                    l: t("renewalDate"),
                    v: selectedSub.subscription?.expiration_date_subs,
                  },
                  {
                    l: t("workshops"),
                    v: selectedSub.subscription?.nbr_workshops,
                  },
                  { l: t("videosabn"), v: selectedSub.subscription?.nbr_video },
                  { l: t("offers"), v: selectedSub.subscription?.nbr_offre },
                ].map((x, i) => (
                  <Grid item xs={6} key={i}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        display: "block",
                        color: COLORS.textSec,
                      }}>
                      {x.l}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: COLORS.textMain }}>
                      {x.v}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Button
                onClick={() => setIsSubDialogOpen(false)}
                sx={{ mt: 3, color: COLORS.secondary, fontWeight: 700 }}>
                {t("close")}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <ConversationDialog
        open={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        conversation={currentConv}
        clientData={client.id_client}
      />
    </Box>
  );
}

export default ClientProfile;
